import { useEffect, useRef } from 'react';
import { Clock, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { useInactivityTimeoutStore } from '@/store/inactivityTimeout';
import { useAuth } from '@/hooks/useAuth';

export function InactivityWarningDialog() {
  const { isWarningVisible, secondsRemaining, resetWarning } = useInactivityTimeoutStore();
  const { signOut } = useAuth();
  const stayLoggedInRef = useRef<HTMLButtonElement>(null);

  // Focus the "Stay Logged In" button when dialog opens
  useEffect(() => {
    if (isWarningVisible) {
      setTimeout(() => stayLoggedInRef.current?.focus(), 100);
    }
  }, [isWarningVisible]);

  const handleStayLoggedIn = () => {
    resetWarning();
  };

  const handleLogOut = async () => {
    resetWarning();
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs} second${secs !== 1 ? 's' : ''}`;
  };

  return (
    <ResponsiveModal open={isWarningVisible} onOpenChange={() => {}}>
      <ResponsiveModalContent className="max-w-md">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <ResponsiveModalTitle>Session Timeout Warning</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Your session is about to expire
              </ResponsiveModalDescription>
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-4">
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-center">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Due to inactivity, you will be automatically logged out in
            </p>
            <p className={`text-3xl font-bold mt-2 ${
              secondsRemaining <= 10 ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'
            }`}>
              {formatTime(secondsRemaining)}
            </p>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            Click "Stay Logged In" or move your mouse to continue working.
          </p>
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleLogOut}
            className="flex-1"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Log Out Now
          </Button>
          <Button
            ref={stayLoggedInRef}
            onClick={handleStayLoggedIn}
            className="flex-1"
          >
            Stay Logged In
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
